import type { DepositOSConfig } from "@gizmolab/depositos";

const ENV_DEFAULTS: Partial<DepositOSConfig> = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || undefined,
};

export type IframeRenderType = "inline" | "popup" | "button";

/**
 * Parse DepositOSConfig from URL search params.
 *
 * Supports two modes:
 *   1. `?config=<base64-json>` — full config as base64-encoded JSON
 *   2. Individual params — `?destChain=42161&destToken=0x...&...`
 *
 * Individual params override values from the base64 blob.
 * Environment variables provide defaults that can be overridden.
 */
export function parseConfigFromURL(): Partial<DepositOSConfig> {
  const params = new URLSearchParams(window.location.search);

  let base: Partial<DepositOSConfig> = { ...ENV_DEFAULTS };

  const configBlob = params.get("config");
  if (configBlob) {
    try {
      const decoded = JSON.parse(atob(configBlob));
      if (decoded && typeof decoded === "object") {
        // Preserve env defaults (especially apiBaseUrl) unless explicitly overridden in blob.
        base = { ...ENV_DEFAULTS, ...(decoded as Partial<DepositOSConfig>) };
      }
    } catch {
      console.warn("[depositOS iframe] Failed to decode `config` param");
    }
  }

  const overrides: Partial<DepositOSConfig> = {};

  const str = (key: string) => params.get(key) ?? undefined;
  const num = (key: string) => {
    const v = params.get(key);
    return v != null ? Number(v) : undefined;
  };
  const bool = (key: string) => {
    const v = params.get(key);
    if (v == null) return undefined;
    return v === "true" || v === "1";
  };

  if (str("destToken")) overrides.destToken = str("destToken");
  if (str("destAddress")) overrides.destAddress = str("destAddress");
  if (str("destTokenSymbol")) overrides.destTokenSymbol = str("destTokenSymbol");
  if (str("destTokenIcon")) overrides.destTokenIcon = str("destTokenIcon");
  if (str("orderRef")) overrides.orderRef = str("orderRef");
  if (str("feeRecipient")) overrides.feeRecipient = str("feeRecipient");
  if (str("apiBaseUrl")) overrides.apiBaseUrl = str("apiBaseUrl");
  if (str("eventsEndpoint")) overrides.eventsEndpoint = str("eventsEndpoint");
  if (str("termsUrl")) overrides.termsUrl = str("termsUrl");
  if (str("supportUrl")) overrides.supportUrl = str("supportUrl");
  if (str("primaryColor")) overrides.primaryColor = str("primaryColor");

  if (num("destChain") != null) overrides.destChain = num("destChain")!;
  if (num("gizmoFeeBps") != null) overrides.gizmoFeeBps = num("gizmoFeeBps");
  if (num("integratorFeeBps") != null) overrides.integratorFeeBps = num("integratorFeeBps");

  if (bool("enablePrivateMode") != null) overrides.enablePrivateMode = bool("enablePrivateMode");
  if (bool("enableWallet") != null) overrides.enableWallet = bool("enableWallet");
  // Deposit-from-anywhere is temporarily disabled globally.
  overrides.enableLayerswap = false;
  if (bool("hideFooter") != null) overrides.hideFooter = bool("hideFooter");
  if (bool("hideBranding") != null) overrides.hideBranding = bool("hideBranding");
  if (bool("hideTerms") != null) overrides.hideTerms = bool("hideTerms");
  if (bool("hideSupport") != null) overrides.hideSupport = bool("hideSupport");
  if (bool("compactMode") != null) overrides.compactMode = bool("compactMode");

  const theme = str("theme");
  if (theme === "light" || theme === "dark") overrides.theme = theme;

  const defaultMode = str("defaultMode");
  if (defaultMode === "standard" || defaultMode === "private") overrides.defaultMode = defaultMode;

  const merged = { ...base, ...overrides };
  const { integratorId: _ignoredIntegrator, dynamicEnvironmentId: _ignoredDynamicEnv, ...safeConfig } = merged;
  return { ...safeConfig, enableLayerswap: false };
}

export function parseRenderTypeFromURL(): IframeRenderType {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("type") ?? params.get("renderType") ?? import.meta.env.VITE_IFRAME_RENDER_TYPE;
  if (raw === "popup" || raw === "button" || raw === "inline") return raw;
  return "inline";
}

export function parseLauncherButtonTextFromURL(): string {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("buttonText") ?? params.get("launcherButtonText") ?? import.meta.env.VITE_IFRAME_BUTTON_TEXT;
  const value = raw?.trim();
  return value && value.length > 0 ? value : "Open depositOS";
}

/** Minimum fields for the widget to render */
export function isValidConfig(cfg: Partial<DepositOSConfig>): cfg is DepositOSConfig {
  return (
    typeof cfg.destChain === "number" &&
    typeof cfg.destToken === "string" &&
    cfg.destToken.length > 0 &&
    typeof cfg.destAddress === "string" &&
    cfg.destAddress.length > 0
  );
}
